import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Backpack,
  X as XIcon,
  Search,
  Sparkles,
  Loader2,
  Crown,
  Zap,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Phoenix Pouch 🎒 — unified slide-out inventory drawer.
 * Tabs: Cards · Perks · Cosmetics · Stickers · Currency
 * Inside each tab: RPG-style grid of tiles, click → detail modal with "Use" action.
 * Empty tab → Egg Nest themed empty state ("Crack open a Phoenix Hatch to fill this nest").
 */
const TABS = [
  { key: "cards",     label: "Cards",       icon: "🎴" },
  { key: "gear",      label: "Gear",        icon: "🛡️" },
  { key: "battle",    label: "Battle",      icon: "⚔️" },
  { key: "perks",     label: "Perks",       icon: "⚡" },
  { key: "cosmetics", label: "Cosmetics",   icon: "🎨" },
  { key: "stickers",  label: "Stickers",    icon: "🐦‍🔥" },
  { key: "currency",  label: "Currency",    icon: "🐉" },
];

const RARITY_STYLES = {
  legendary: "border-[#D4AF37] bg-gradient-to-br from-[#D4AF37]/15 to-transparent",
  inferno:   "border-orange-500 bg-gradient-to-br from-orange-500/15 to-transparent",
  forged:    "border-amber-500 bg-gradient-to-br from-amber-500/15 to-transparent",
  rare:      "border-[#7C3AED] bg-gradient-to-br from-[#7C3AED]/15 to-transparent",
  uncommon:  "border-emerald-500 bg-gradient-to-br from-emerald-500/15 to-transparent",
  common:    "border-zinc-700 bg-[#0A0A0A]",
};
const RARITY_LABEL_STYLES = {
  legendary: "text-[#D4AF37]",
  inferno:   "text-orange-400",
  forged:    "text-amber-400",
  rare:      "text-[#A78BFA]",
  uncommon:  "text-emerald-400",
  common:    "text-neutral-500",
};

export default function InventoryBag({ open, onClose }) {
  const { user, refreshUser } = useAuth();
  const [bag, setBag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("cards");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("rarity"); // rarity | type | recent
  const [picked, setPicked] = useState(null); // item being inspected

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/inventory`);
      setBag(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (open) {
      load();
      // Mark inventory as "seen" so the red badge clears. Fire-and-forget —
      // the request is idempotent and a transient failure just leaves the
      // badge alone, which is the right fallback. Once it succeeds we
      // broadcast a window event so the header button + bottom dock can
      // re-poll their unread counts immediately instead of waiting for
      // their 30-45s polling cycle.
      axios
        .post(`${API}/inventory/mark-seen`)
        .then(() => window.dispatchEvent(new Event("omni:inventory-seen")))
        .catch(() => {});
    }
  }, [open, load]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!user) return null;

  // ----- Filter + sort current tab -----
  // 🪦 Jul 2026 owner directive: fully purge Rename Card / Handle Card
  // from every UI surface. Legacy inventory rows are hidden here.
  const items = (bag?.categories?.[tab] || [])
    .filter((it) => !["rename_card", "handle_rename_card"].includes(it.id))
    .slice();
  const q = search.trim().toLowerCase();
  const filtered = items.filter(
    (it) =>
      !q ||
      it.name?.toLowerCase().includes(q) ||
      it.id?.toLowerCase().includes(q) ||
      (it.description || "").toLowerCase().includes(q)
  );
  if (sort === "rarity") {
    const rank = { legendary: 0, inferno: 1, forged: 2, rare: 3, uncommon: 4, common: 5 };
    filtered.sort((a, b) => (rank[a.rarity] ?? 9) - (rank[b.rarity] ?? 9));
  } else if (sort === "type") {
    filtered.sort((a, b) => (a.id || "").localeCompare(b.id || ""));
  } else if (sort === "recent") {
    filtered.sort((a, b) => (b.won_at || "").localeCompare(a.won_at || ""));
  }

  // ----- Use an EXP Charge (Feb 2026) -----
  async function useExpCharge(item) {
    // 🪶 Oct 8 2026 — Optimistic decrement so the inventory count ticks
    // down the moment Use is clicked. The server IS the source of truth
    // (`$unset` + `$pull` in /api/inventory/use-exp-charge), but the UI
    // would otherwise display stale numbers until the follow-up load()
    // round-trip completes.
    setBag((prev) => {
      if (!prev || !Array.isArray(prev.items)) return prev;
      const nextItems = prev.items.map((it) => {
        if (it.id !== item.id) return it;
        const cur = Number(it.count || 0);
        return { ...it, count: Math.max(0, cur - 1) };
      }).filter((it) => Number(it.count || 0) > 0 || it._tab === "currency");
      return { ...prev, items: nextItems };
    });
    try {
      const { data } = await axios.post(`${API}/inventory/use-exp-charge`, { item_id: item.id });
      const lvls = (data?.hero_class?.leveled_up || []);
      toast.success(`⚡ +${data.exp_granted} XP`, {
        description: lvls.length
          ? `LEVEL UP! → Lv ${lvls[lvls.length - 1]}`
          : `Hero-Class XP applied (Lv ${data?.hero_class?.level || "?"})`,
        duration: 5500,
      });
      setPicked(null);
      await refreshUser?.();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't burn the charge.");
      // Rollback optimistic decrement on failure — re-fetch gets the true count.
      await load();
    }
  }

  async function equipCosmetic(item) {
    try {
      await axios.post(`${API}/lounge/equip`, { slot: item.slot, item_id: item.id });
      toast.success(`✨ Equipped ${item.name}`);
      setPicked(null);
      await refreshUser?.();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Equip failed.");
    }
  }

  async function unequipCosmetic(item) {
    try {
      await axios.post(`${API}/lounge/equip`, { slot: item.slot, item_id: null });
      toast.success(`Unequipped ${item.name}`);
      setPicked(null);
      await refreshUser?.();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unequip failed.");
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="bag-backdrop"
        onClick={onClose}
        className={`fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
      />

      {/* Drawer — slides in from right */}
      <aside
        data-testid="inventory-bag"
        aria-hidden={!open}
        className={`fixed top-0 right-0 z-[91] h-full w-full sm:w-[440px] bg-[#0A0A0A] border-l-2 border-[#D4AF37] transform transition-transform duration-300 ease-out shadow-[0_0_60px_rgba(212,175,55,0.25)] flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="p-5 border-b border-zinc-800 flex items-start justify-between gap-3 shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold flex items-center gap-1.5">
              <Backpack size={11} /> Phoenix Pouch
            </div>
            <h2 className="font-display text-3xl text-white leading-tight">
              Your <span className="text-[#D4AF37]">Bag.</span>
            </h2>
            <div className="text-[11px] text-neutral-500 mt-1 tabular-nums">
              {bag?.totals?.all_items || 0} items · {bag?.totals?.unused_actionable || 0} unused
            </div>
            <a
              href="/marketplace"
              data-testid="bag-market-link"
              className="inline-flex items-center gap-1 mt-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#FCD34D] border border-[#D4AF37]/50 hover:bg-[#D4AF37] hover:text-black px-2 py-1 transition-colors"
            >
              🛒 Marketplace
            </a>
          </div>
          <button
            type="button"
            data-testid="bag-close"
            onClick={onClose}
            className="w-9 h-9 border border-zinc-800 hover:border-[#D4AF37] flex items-center justify-center text-neutral-400 hover:text-[#D4AF37]"
            aria-label="Close inventory"
          >
            <XIcon size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-800 shrink-0">
          {TABS.map((t) => {
            const count = t.key === "currency" ? null : (bag?.categories?.[t.key] || []).length;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`bag-tab-${t.key}`}
                onClick={() => { setTab(t.key); setSearch(""); }}
                className={`flex-1 py-3 text-[10px] uppercase tracking-widest font-bold border-b-2 transition-colors ${
                  tab === t.key
                    ? "border-[#D4AF37] text-white bg-[#D4AF37]/5"
                    : "border-transparent text-neutral-500 hover:text-neutral-300"
                }`}
              >
                <span className="block text-base leading-none mb-0.5">{t.icon}</span>
                <span>{t.label}</span>
                {count !== null && count > 0 && (
                  <span className="ml-1 text-[#D4AF37] tabular-nums">·{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Search + sort (hidden on currency tab) */}
        {tab !== "currency" && (
          <div className="px-4 py-3 border-b border-zinc-800 flex gap-2 shrink-0">
            <div className="relative flex-1">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-600" />
              <input
                data-testid="bag-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${tab}…`}
                className="input-omni w-full pl-9 text-sm"
              />
            </div>
            <select
              data-testid="bag-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="input-omni text-xs px-2"
              aria-label="Sort"
            >
              <option value="rarity">Rarity</option>
              <option value="type">Type</option>
              <option value="recent">Recent</option>
            </select>
          </div>
        )}

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center text-neutral-500 py-12">
              <Loader2 className="animate-spin inline mr-2" size={14} /> Loading…
            </div>
          ) : tab === "currency" ? (
            <CurrencyPanel totals={bag?.totals} />
          ) : filtered.length === 0 ? (
            <EggNestEmpty tab={tab} hasSearch={!!q} />
          ) : (
            <div className="grid grid-cols-3 gap-2.5">
              {filtered.map((it, i) => (
                <BagTile
                  key={`${it.id}-${i}`}
                  item={it}
                  onClick={() => setPicked({ ...it, _tab: tab })}
                />
              ))}
              {/* Pad with empty slots to keep the grid looking "filled" */}
              {Array.from({ length: Math.max(0, 6 - (filtered.length % 6)) % 6 }).map((_, i) => (
                <EmptySlot key={`empty-${i}`} />
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* Item detail modal */}
      {picked && (
        <ItemDetailModal
          item={picked}
          onClose={() => setPicked(null)}
          onUseExpCharge={useExpCharge}
          onEquip={equipCosmetic}
          onUnequip={unequipCosmetic}
        />
      )}
    </>
  );
}

/* ============ Tile ============ */
function BagTile({ item, onClick }) {
  const rarityCls = RARITY_STYLES[item.rarity] || RARITY_STYLES.common;
  // 🆕 "Last Acquired" — show a fresh chip if won within the last 24h.
  const isFresh = (() => {
    if (!item.won_at) return false;
    const t = new Date(item.won_at).getTime();
    if (Number.isNaN(t)) return false;
    return (Date.now() - t) < 24 * 60 * 60 * 1000;
  })();
  return (
    <button
      type="button"
      data-testid={`bag-tile-${item.id}`}
      onClick={onClick}
      className={`relative aspect-square border-2 ${rarityCls} flex flex-col items-center justify-center p-2 hover:scale-[1.04] hover:brightness-110 transition-transform`}
      title={item.name}
    >
      <span className="text-3xl leading-none">{item.icon}</span>
      <span className="block text-[9px] uppercase tracking-wider text-neutral-300 mt-1.5 text-center line-clamp-2 leading-tight">
        {item.name}
      </span>
      {item.count > 1 && (
        <span
          data-testid={`bag-tile-count-${item.id}`}
          className="absolute top-1 right-1 bg-[#D4AF37] text-black text-[10px] font-bold tabular-nums px-1.5 py-0.5 leading-none"
        >
          ×{item.count}
        </span>
      )}
      {item.equipped && (
        <span className="absolute top-1 left-1 bg-emerald-500 text-black text-[8px] font-bold uppercase tracking-widest px-1 py-0.5 leading-none">
          On
        </span>
      )}
      {isFresh && (
        <span
          data-testid={`bag-tile-fresh-${item.id}`}
          className="absolute bottom-1 left-1 right-1 mx-auto w-fit bg-fuchsia-500 text-white text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 leading-none animate-pulse">
          🆕 NEW
        </span>
      )}
    </button>
  );
}

function EmptySlot() {
  return (
    <div
      aria-hidden="true"
      className="aspect-square border border-dashed border-zinc-900 bg-black/40"
    />
  );
}

/* ============ Egg Nest Empty State ============ */
function EggNestEmpty({ tab, hasSearch }) {
  const copy = {
    cards:     { msg: "No cards yet.",     hint: "Crack a Phoenix Hatch or spin the Wheel to earn cards & drops." },
    perks:     { msg: "No perks owned.",   hint: "Buy from the Phoenix Lounge or win them from the Wheel/Hatch." },
    cosmetics: { msg: "No cosmetics yet.", hint: "Visit the Phoenix Lounge to unlock frames, badges, and titles." },
    stickers:  { msg: "No stickers yet.",  hint: "Stickers drop from the Wheel and the Phoenix Hatch." },
  }[tab] || { msg: "Empty.", hint: "" };
  return (
    <div data-testid={`bag-empty-${tab}`} className="text-center py-10 px-4">
      {/* Egg Nest illustration */}
      <div className="relative inline-block mb-4">
        <svg viewBox="0 0 120 90" width="120" height="90" className="mx-auto" aria-hidden="true">
          {/* Nest */}
          <ellipse cx="60" cy="72" rx="56" ry="14" fill="#3a2410" />
          <path d="M6 70 Q 18 60 28 70 M 32 68 Q 44 56 56 68 M 60 70 Q 72 58 84 70 M 88 68 Q 100 56 112 68" stroke="#5a3a18" strokeWidth="2" fill="none" />
          {/* Three eggs */}
          <ellipse cx="40" cy="58" rx="11" ry="14" fill="#2a2a2a" stroke="#1a1a1a" strokeWidth="1" />
          <ellipse cx="60" cy="52" rx="12" ry="16" fill="#3a3a3a" stroke="#1a1a1a" strokeWidth="1" />
          <ellipse cx="80" cy="58" rx="11" ry="14" fill="#2a2a2a" stroke="#1a1a1a" strokeWidth="1" />
        </svg>
      </div>
      <p className="font-display text-xl text-white mb-1">
        {hasSearch ? "Nothing matches." : copy.msg}
      </p>
      <p className="text-xs text-neutral-500 max-w-[280px] mx-auto leading-relaxed">
        {hasSearch ? "Try a different search term." : copy.hint}
      </p>
    </div>
  );
}

/* ============ Currency Panel ============ */
function CurrencyPanel({ totals }) {
  return (
    <div className="space-y-3" data-testid="bag-currency">
      <div className="border-2 border-[#D4AF37] bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-5 text-center">
        <div className="text-4xl mb-2 leading-none">🐦‍🔥</div>
        <div className="font-display text-4xl text-[#D4AF37] tabular-nums">
          {(totals?.tokens || 0).toLocaleString()}
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 mt-1 font-bold">
          Phoenix Tokens
        </div>
        <p className="text-[11px] text-neutral-500 mt-3 italic">
          Spend in the Wheel, Phoenix Lounge, and Arcade.
        </p>
      </div>
      <div className="border-2 border-emerald-700 bg-gradient-to-br from-emerald-900/30 to-transparent p-5 text-center">
        <div className="text-4xl mb-2 leading-none">☯️</div>
        <div className="font-display text-4xl text-emerald-300 tabular-nums">
          {(totals?.omni_dollars || 0).toLocaleString()}
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 mt-1 font-bold">
          Omni Dollars
        </div>
        <p className="text-[11px] text-neutral-500 mt-3 italic">
          Spend in the Shop on real merch.
        </p>
      </div>
      {/* ☯️ Oct 2026 — Soul Keys always-visible chip (Roc #Soul-Key regression).
          Shows the player's live key count so they never think the item vanished;
          links to /arena where the swap panel + purchase buttons live. */}
      <a
        href="/arena"
        data-testid="bag-currency-soul-keys"
        className="block border-2 border-amber-700 bg-gradient-to-br from-amber-900/30 to-transparent p-5 text-center hover:border-amber-400 transition-colors"
      >
        <div className="text-4xl mb-2 leading-none">🗝️</div>
        <div className="font-display text-4xl text-amber-200 tabular-nums">
          {(totals?.soul_keys || 0).toLocaleString()}
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 mt-1 font-bold">
          Soul Keys
        </div>
        <p className="text-[11px] text-neutral-500 mt-3 italic">
          Burn 1 to swap your hero class — Warrior · Mage · Archer · Ash Rogue.
          Keep your level, XP and rebirth.
        </p>
      </a>
    </div>
  );
}

/* ============ Item Detail Modal ============ */
function ItemDetailModal({ item, onClose, onUseExpCharge, onEquip, onUnequip }) {
  const rarityCls = RARITY_STYLES[item.rarity] || RARITY_STYLES.common;
  const rarityLabel = RARITY_LABEL_STYLES[item.rarity] || RARITY_LABEL_STYLES.common;

  // Action variants
  const isExpCharge = (item.id || "").startsWith("exp_charge_");
  const isCosmetic = item._tab === "cosmetics";

  return (
    <div
      data-testid="bag-detail-modal"
      onClick={onClose}
      className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-[#0F0F0F] border-2 max-w-sm w-full p-6 max-h-[88vh] overflow-y-auto ${
          item.rarity === "legendary" ? "border-[#D4AF37]" : item.rarity === "rare" ? "border-[#7C3AED]" : "border-zinc-700"
        }`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className={`text-[10px] uppercase tracking-[0.3em] font-bold ${rarityLabel}`}>
            {item.rarity || "common"}
          </div>
          <button
            type="button"
            data-testid="bag-detail-close"
            onClick={onClose}
            className="text-neutral-500 hover:text-white"
            aria-label="Close"
          >
            <XIcon size={20} />
          </button>
        </div>

        <div className={`text-7xl my-3 leading-none text-center ${rarityCls.includes("from-[#D4AF37]") ? "drop-shadow-[0_0_24px_rgba(212,175,55,0.6)]" : ""}`}>
          {item.icon}
        </div>
        <h3 className="font-display text-2xl text-white text-center mb-1">{item.name}</h3>
        {item.count > 1 && (
          <div className="text-center text-[#D4AF37] text-sm font-bold tabular-nums mb-2">
            Owned: ×{item.count}
          </div>
        )}
        {item.equipped && (
          <div className="text-center mb-2">
            <span className="text-[10px] bg-emerald-500 text-black px-2 py-0.5 uppercase tracking-widest font-bold">
              Equipped
            </span>
          </div>
        )}
        {item.description && (
          <p className="text-sm text-neutral-300 text-center my-3 leading-relaxed">
            {item.description}
          </p>
        )}
        {item.set && (
          <p className="text-[10px] uppercase tracking-widest text-neutral-500 text-center mb-2">
            {item.set} set
          </p>
        )}

        {/* Action: EXP Charge → tap to burn for Hero-Class XP */}
        {isExpCharge ? (
          <div className="mt-4 space-y-2">
            <button
              type="button"
              data-testid="bag-exp-charge-use"
              onClick={() => onUseExpCharge(item)}
              className="btn-omni btn-red w-full"
            >
              <Sparkles size={14} /> Burn Charge · +{item.exp_amount || (item.id === "exp_charge_500" ? 500 : item.id === "exp_charge_300" ? 300 : 100)} XP
            </button>
            <p className="text-[10px] text-neutral-500 text-center leading-snug">
              EXP Charges apply Hero-Class XP on tap. Stack them for level-thresholds —
              they don&apos;t auto-trigger.
            </p>
          </div>
        ) : isCosmetic ? (
          <div className="mt-4">
            {item.equipped ? (
              <button
                type="button"
                data-testid="bag-unequip-btn"
                onClick={() => onUnequip(item)}
                className="btn-omni btn-outline-gold w-full"
              >
                Unequip
              </button>
            ) : (
              <button
                type="button"
                data-testid="bag-equip-btn"
                onClick={() => onEquip(item)}
                className="btn-omni btn-red w-full"
              >
                <Crown size={14} /> Equip
              </button>
            )}
          </div>
        ) : (
          <div className="mt-4 text-center">
            <p className="text-[11px] text-neutral-500 italic">
              Use this perk via the Phoenix Lounge inventory section.
            </p>
          </div>
        )}

        {item.won_at && (
          <p className="text-[10px] uppercase tracking-widest text-neutral-600 mt-4 text-center">
            Won {new Date(item.won_at).toLocaleDateString()}
            {item.source && ` · from ${item.source.replace("_", " ")}`}
          </p>
        )}
      </div>
    </div>
  );
}

/* ============ Header trigger button ============ */
export function InventoryBagButton({ onOpen }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [pulse, setPulse] = useState(false);
  const prevCountRef = useState({ current: null })[0];

  const fetchCount = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/inventory`);
      // `unseen` = new items since last bag open (clears on open).
      // Falls back to `unused_actionable` for older clients/servers.
      const n = data?.totals?.unseen ?? data?.totals?.unused_actionable ?? 0;
      if (prevCountRef.current !== null && n > prevCountRef.current) {
        setPulse(true);
        setTimeout(() => setPulse(false), 4000);
      }
      prevCountRef.current = n;
      setUnread(n);
    } catch {
      // silent
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 45000);
    // 🎒 Re-fetch instantly when the bag drawer dispatches "seen" so the
    // red badge clears the moment the user opens the bag (vs. waiting up
    // to 45s for the next poll).
    const onSeen = () => { setUnread(0); fetchCount(); };
    window.addEventListener("omni:inventory-seen", onSeen);
    return () => {
      clearInterval(id);
      window.removeEventListener("omni:inventory-seen", onSeen);
    };
  }, [fetchCount]);

  if (!user) return null;

  return (
    <button
      type="button"
      data-testid="bag-open-btn"
      onClick={onOpen}
      className={`relative w-10 h-10 border border-zinc-800 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors flex items-center justify-center text-neutral-300 ${
        pulse ? "ph-bag-pulse" : ""
      }`}
      title={`Phoenix Pouch · ${unread} unused`}
      aria-label={`Open inventory bag, ${unread} unused items`}
    >
      <Backpack size={16} />
      {unread > 0 && (
        <span
          data-testid="bag-unread-count"
          className="absolute -top-1.5 -right-1.5 bg-[#DC2626] text-white text-[10px] font-bold tabular-nums w-5 h-5 flex items-center justify-center rounded-full border border-black leading-none"
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
      <style>{`
        @keyframes ph-bag-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.7); }
          50%      { box-shadow: 0 0 0 10px rgba(212, 175, 55, 0); }
        }
        .ph-bag-pulse { animation: ph-bag-pulse 1.6s ease-out infinite; }
      `}</style>
    </button>
  );
}
